import React, { useEffect, useState } from "react";
import { ContentItem } from "@/types/content";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Lock, Globe, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
interface ContentShareDialogProps {
  content: ContentItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVisibilityChange: (contentId: string, visibility: "private" | "public") => void;
}

export const ContentShareDialog = ({
  content,
  open,
  onOpenChange,
  onVisibilityChange,
}: ContentShareDialogProps) => {
  const { t, isRtl } = useLanguage();
  const [localVisibility, setLocalVisibility] = useState<"private" | "public">(
    content?.visibility ?? "private"
  );
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLocalVisibility(content?.visibility ?? "private");
  }, [content?.visibility]);

  const handleSelect = async (visibility: "private" | "public") => {
    if (!content) return;

    setLocalVisibility(visibility);

    const { error } = await supabase
      .from("content")
      .update({ visibility })
      .eq("id", content.id);

    if (error) {
      toast.error(t("contentShare.updateError"));
      setLocalVisibility(content.visibility ?? "private");
      return;
    }

    toast.success(visibility === "public" ? t("contentShare.nowPublic") : t("contentShare.nowPrivate"));
    onVisibilityChange(content.id, visibility);
  };

  const handleCopy = () => {
    if (!content) return;
    const url = `${window.location.origin}/content/${content.id}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!content) return null;

  const shareUrl = `${window.location.origin}/content/${content.id}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" dir={isRtl ? "rtl" : "ltr"}>
      
        <DialogHeader>
          <DialogTitle>{t("contentShare.title")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Visibility Options */}
          <div className="space-y-2">
            <p className="text-sm font-medium">{t("contentShare.visibility")}</p>
            <div className="grid gap-2">
              {/* Private Option */}
              <button
                onClick={() => handleSelect("private")}
                className={`flex items-start gap-3 rounded-lg border p-3 text-start transition ${
                  localVisibility === "private"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <Lock className="h-5 w-5 mt-0.5 text-muted-foreground" />
                <div className="flex-1">
                  <p className="font-medium text-sm">{t("contentShare.private")}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("contentShare.privateDescription")}
                  </p>
                </div>
                {localVisibility === "private" && (
                  <Check className="h-5 w-5 text-primary" />
                )}
              </button>

              {/* Public Option */}
              <button
                onClick={() => handleSelect("public")}
                className={`flex items-start gap-3 rounded-lg border p-3 text-start transition ${
                  localVisibility === "public"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <Globe className="h-5 w-5 mt-0.5 text-muted-foreground" />
                <div className="flex-1">
                  <p className="font-medium text-sm">{t("contentShare.public")}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("contentShare.publicDescription")}
                  </p>
                </div>
                {localVisibility === "public" && (
                  <Check className="h-5 w-5 text-primary" />
                )}
              </button>
            </div>
          </div>

          {/* Share Link */}
          <div className="space-y-2">
            <p className="text-sm font-medium">{t("contentShare.shareLink")}</p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={shareUrl}
                readOnly
                dir="ltr"
                className="flex-1 rounded-md border bg-muted px-3 py-2 text-sm"
              />
              <button
                onClick={handleCopy}
                disabled={localVisibility === "private"}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {copied ? t("contentShare.copied") : t("contentShare.copy")}
              </button>
            </div>
            {localVisibility === "private" && (
              <p className="text-xs text-muted-foreground">
                {t("contentShare.enableSharingHint")}
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
